import React from 'react';

import { css, cx } from '@emotion/css';
import {
  Card,
  CardContent,
  CardHeader,
  Theme,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '@/assets/fonts';
import { Icons } from '@/assets/icons';
import { AlertIconNotification } from '@/components/notifications/AlertIconNotification';
import { useStyles } from '@/hooks/useStyles';
import {
  AlertCategory,
  AlertSummary,
  alertCategoriesTranslations,
} from '@/models/AlertModel';
import { Queries } from '@/queries/Queries';

export type ActiveNotificationsProps = {
  patientId: string;
  className?: string;
};

export const AlertsList: React.FC<ActiveNotificationsProps> = ({
  patientId,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const alerts = Queries.alerts.useListPatientAlerts(patientId);

  return (
    <Card className={className} elevation={0}>
      <CardHeader title={t('notifications.title')} />
      <CardContent className={styles.cardBody}>
        {alerts.length > 0 ? (
          <NotificationsList notifications={alerts} patientId={patientId} />
        ) : (
          <Typography variant="body" className={styles.noNotif}>
            {t('notifications.none')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const NotificationsList = ({
  notifications,
  patientId,
}: {
  notifications: AlertSummary[];
  patientId: string;
}) => (
  <>
    {notifications.map((notification, idx) => (
      <Notification
        key={idx}
        notification={notification}
        patientId={patientId}
      />
    ))}
  </>
);

const Notification = ({
  notification,
  patientId,
}: {
  notification: AlertSummary;
  patientId: string;
}) => {
  const styles = useStyles(makeStyles);
  const dismissAlert = Queries.alerts.useDismissAlert();

  return (
    <div className={styles.notifLink}>
      <AlertIconNotification />
      <InfoNotification category={notification.category} />
      <HoverButton
        id={`notification-dismiss-${notification.id}`}
        data-action="notification-dismiss"
        data-notification={notification.id}
        className={cx({
          [styles.disabledHoverButton]: !notification.can_resolve,
        })}
        openModal={() =>
          notification.can_resolve &&
          dismissAlert.mutate({
            patientId: patientId,
            alertId: notification.id,
          })
        }
      />
    </div>
  );
};

const InfoNotification = ({ category }: { category: AlertCategory }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.notifContent}>
      <Typography variant="bodySmall">
        {t(alertCategoriesTranslations[category])}
      </Typography>
    </div>
  );
};

/**
 * @deprecated Use MUI Button instead
 */
const HoverButton = ({
  id,
  'data-action': dataAction,
  openModal,
  className,
}: {
  id?: string;
  'data-action'?: string;
  openModal: () => void;
  className?: string;
}) => {
  const styles = useStyles(makeStyles);
  const handleClick = async (evt: React.MouseEvent) => {
    // prevent Link to redirect
    evt.preventDefault();

    // prevent Tooltip to autoclose
    evt.stopPropagation();

    openModal();
  };

  return (
    <div
      id={id}
      data-action={dataAction}
      className={cx('hover-button', className, styles.hoverButton)}
      onClick={handleClick}
    >
      <Icons.check className={styles.icon} />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  notifLink: css`
    display: flex;
    padding: ${theme.spacing(4)};
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(4)};
    border-radius: ${theme.spacing(1)};
    .hover-button {
      visibility: hidden;
      opacity: 0;
      cursor: pointer;
    }
    :hover {
      .hover-button {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.3s;
      }
      background-color: ${theme.palette.background.default};
      transition: background-color 0.3s;
    }
  `,
  notifContent: css`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    margin-left: ${theme.spacing(4)};
    justify-content: flex-start;
  `,
  noNotif: css`
    margin: ${theme.spacing(4)} 0;
    font-style: italic;
    color: ${theme.palette.text.secondary};
  `,
  cardBody: css`
    padding: ${theme.spacing(4)} ${theme.spacing(8)} ${theme.spacing(8)}
      ${theme.spacing(8)};
    display: flex;
    flex-direction: column;
  `,
  disabledHoverButton: css`
    visibility: hidden !important;
  `,
  hoverButton: css`
    flex: 0 0;
    display: flex;
    background-color: ${theme.palette.primary.main};
    border-radius: 100%;
    height: fit-content;
    width: fit-content;
    align-self: center;
    padding: ${theme.spacing(4)};
    :hover {
      background-color: ${theme.palette.primary.light};
    }
  `,
  icon: css`
    color: ${theme.palette.common.white};
    width: ${FontSizes.bodyLarge};
    height: ${FontSizes.bodyLarge};
  `,
});
